import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import { updateBulk } from 'redux/actions/Menu/menuV2';
import { linkedWithProvider } from 'helpers/linkedWithProvider';
import {
  SET_LINK_PROVIDER_VISIBILITY,
  SET_LINK_PROVIDER,
  SET_LOADING,
} from 'redux/actions/types';
import { PROVIDER_CONNECTED } from 'constants/providerStatuses';
import Private from 'components/Private';
import { isPermissionWithCompanyType } from 'hooks/useCompanyType';
import ConfirmProviderMenu from 'components/Confirm/ConfirmProviderMenu';
import { isWebsiteConnected } from 'utils/checkAccessDenied';

const UpdateProviderMenuButton = ({ buttonId = 'UpdateProviderMenu' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const { menu, loading, formProduct } = useSelector(({ menu }) => menu);
  const { items } = useSelector(({ providers }) => providers);
  const { isTypeSingle } = isPermissionWithCompanyType();
  // The menu-level `bulkInfo.isSaved` flag only reflects menu-level changes and
  // is not updated by the backend when individual products/categories/modifiers
  // are changed (e.g. reordering modifiers). Those changes are persisted on the
  // child-level `bulkInfo.isSaved` flags, which also drive the row highlight.
  // Aggregate all of them (plus the currently open product) so the button stays
  // consistent with the highlight and survives a page refresh.
  const hasUnsavedChanges = useMemo(() => {
    if (!menu?._id) return false;

    const allSaved = (list) =>
      !list?.length || list.every((item) => item?.bulkInfo?.isSaved);

    const categoriesSaved = allSaved(menu.categories);
    const productsSaved = (menu.categories || []).every((category) =>
      allSaved(category?.products)
    );
    const modifiersSaved = allSaved(menu.modifiersAll);
    const formProductSaved =
      !formProduct?._id || formProduct?.bulkInfo?.isSaved !== false;

    return (
      !menu.bulkInfo?.isSaved ||
      !categoriesSaved ||
      !productsSaved ||
      !modifiersSaved ||
      !formProductSaved
    );
  }, [menu, formProduct]);

  const linked = useMemo(() => {
    return (
      menu?.connectedProviders && linkedWithProvider(menu.connectedProviders)
    );
  }, [menu]);

  const targetConnectedProviders = useMemo(() => {
    const isWebsite = isWebsiteConnected(items);
    const res = { website: isWebsite };
    items.forEach((providerItem) => {
      res[providerItem.srv] = providerItem.status == PROVIDER_CONNECTED;
    });
    return res;
  }, [items]);

  const getConnectedDefaultProvider = () => {
    dispatch({ type: SET_LINK_PROVIDER_VISIBILITY, payload: true });
    dispatch({
      type: SET_LINK_PROVIDER,
      payload: {
        connectedProviders: menu?.connectedProviders,
        targetConnectedProviders: targetConnectedProviders,
        target: 'menu',
      },
    });
  };

  const handleUpdateProvider = () => setShowModal(true);

  const handleBulk = () => {
    dispatch({ type: SET_LOADING, payload: true });
    if (linked) {
      dispatch(updateBulk());
      setShowModal(false);
    } else {
      getConnectedDefaultProvider();
      dispatch({ type: SET_LOADING, payload: false });
      setShowModal(false);
    }
  };

  const handleConfirm = () => handleBulk();
  const handleClose = () => setShowModal(false);

  // const checkWebsite = useMemo(() => {
  //   return (
  //     menu?.connectedProviders &&
  //     checkLinkedProviderWebsite(menu.connectedProviders, providers)
  //   );
  // }, [menu, providers]);

  if (!isTypeSingle) {
    return null;
  }

  return (
    <>
      {showModal && (
        <ConfirmProviderMenu
          handleClose={handleClose}
          showModal={showModal}
          handleConfirm={handleConfirm}
        />
      )}
      <Private permission="MENU_EDIT" disabled={true}>
        <Button
          id={buttonId}
          disabled={loading || (!hasUnsavedChanges && linked)}
          onClick={linked ? handleUpdateProvider : handleBulk}
          sx={{ ml: 2, minWidth: '240px' }}
          variant="contained"
          startIcon={linked ? <UpdateIcon /> : <LinkOffIcon />}
        >
          {loading
            ? t('menu.updating')
            : linked
            ? t('menu.update_provider_menus')
            : t('menu.connect_provider_menus')}
        </Button>
      </Private>
    </>
  );
};

export default UpdateProviderMenuButton;
